import type { Note } from './battle-loop';

// Boss BGM — 45 s, A minor, high tension, ~150 bpm, square wave + low-freq drum bursts
// Quarter note at 150 bpm = 0.40 s, eighth = 0.20 s, half = 0.80 s
// 4/4 → bar = 1.60 s.  45 s ≈ 28 bars.
// Chord progression cycling every 4 bars (6 cycles + 4-bar coda):
//   Bars 1-2: Am (A3 C4 E4)
//   Bars 3-4: F  (F3 A3 C4) → tension
//   then faster alternation in cycle 2+

// A minor scale frequencies
const A3  = 220.00;
const B3  = 246.94;
const C4  = 261.63;
const D4  = 293.66;
const E4  = 329.63;
const F4  = 349.23;
const G4  = 392.00;
const A4  = 440.00;
const B4  = 493.88;
const C5  = 523.25;
const D5  = 587.33;
const E5  = 659.25;
const F5  = 698.46;
const G5  = 783.99;

// Bass octave
const A2  = 110.00;
const E3  = 164.81;
const F3  = 174.61;
const G3  = 196.00;
const D3  = 146.83;

// ── "Drum" burst: low-freq oscillator pulse simulating percussion ─────────────
// Uses a very low frequency (55 Hz ≈ bass drum) with short duration + high gain,
// wave type 'square' for hard attack (stored in wave field).
// These appear on beats 1 and 3 (every 0.80 s in 150 bpm 4/4).
function drumBeat(startSec: number): Note {
  return { freq: 55, startSec, durationSec: 0.08, gain: 0.30, wave: 'square' };
}

// Snare-like accent: higher low-freq (110 Hz) on beat 3
function snareBeat(startSec: number): Note {
  return { freq: 110, startSec, durationSec: 0.06, gain: 0.20, wave: 'square' };
}

// ── Build one 4-bar block ─────────────────────────────────────────────────────
// bar = 1.60 s, beat = 0.40 s
const BAR = 1.60;
const Q = 0.40;  // quarter
const E = 0.20;  // eighth
const H = 0.80;  // half

// Returns drum hits for a 4-bar block starting at offset
function drums4(offset: number): Note[] {
  const hits: Note[] = [];
  for (let bar = 0; bar < 4; bar++) {
    const b = offset + bar * BAR;
    hits.push(drumBeat(b + 0.00));          // beat 1
    hits.push(snareBeat(b + H));             // beat 3 (syncopated snare)
    hits.push(drumBeat(b + BAR - Q));        // beat 4 (anticipation)
    // Syncopated extra beat every other bar
    if (bar % 2 === 1) hits.push(drumBeat(b + Q));  // beat 2 of odd bars
  }
  return hits;
}

// ── Melody sections ───────────────────────────────────────────────────────────

// Cycle 1 (bars 1-4, offset 0): Am → F, syncopated 16th-note feel
function cycle1(offset: number): Note[] {
  return [
    // Bar 1 — Am — stab + run
    { freq: A3, startSec: offset + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: C4, startSec: offset + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: E4, startSec: offset + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: A4, startSec: offset + 0.80, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: G4, startSec: offset + 1.00, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: E4, startSec: offset + 1.20, durationSec: Q,  gain: 0.30, wave: 'square' },

    // Bar 2 — Am — descend with syncopation
    { freq: D4, startSec: offset + BAR + 0.00, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: E4, startSec: offset + BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: C4, startSec: offset + BAR + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: A3, startSec: offset + BAR + 0.80, durationSec: H,  gain: 0.28, wave: 'square' },

    // Bar 3 — F — tense
    { freq: F3, startSec: offset + 2*BAR + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: A3, startSec: offset + 2*BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: C4, startSec: offset + 2*BAR + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: F4, startSec: offset + 2*BAR + 0.80, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: E4, startSec: offset + 2*BAR + 1.00, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: D4, startSec: offset + 2*BAR + 1.20, durationSec: Q,  gain: 0.28, wave: 'square' },

    // Bar 4 — G/F — build
    { freq: G3, startSec: offset + 3*BAR + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: B3, startSec: offset + 3*BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: D4, startSec: offset + 3*BAR + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: G4, startSec: offset + 3*BAR + 0.80, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: F4, startSec: offset + 3*BAR + 1.00, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: E4, startSec: offset + 3*BAR + 1.20, durationSec: Q,  gain: 0.28, wave: 'square' },

    ...drums4(offset),
  ];
}

// Cycle 2 (bars 5-8, offset 6.4): faster alternation Am/Dm
function cycle2(offset: number): Note[] {
  return [
    // Bar 1 — Am upper octave aggression
    { freq: A4, startSec: offset + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: E5, startSec: offset + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: A4, startSec: offset + 0.40, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: G4, startSec: offset + 0.60, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: E4, startSec: offset + 0.80, durationSec: Q,  gain: 0.28, wave: 'square' },
    { freq: A4, startSec: offset + 1.20, durationSec: Q,  gain: 0.30, wave: 'square' },

    // Bar 2 — Am — staccato run down
    { freq: G4, startSec: offset + BAR + 0.00, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: E4, startSec: offset + BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: D4, startSec: offset + BAR + 0.40, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: C4, startSec: offset + BAR + 0.60, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: A3, startSec: offset + BAR + 0.80, durationSec: H,  gain: 0.30, wave: 'square' },

    // Bar 3 — Dm
    { freq: D3, startSec: offset + 2*BAR + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: F3, startSec: offset + 2*BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: A3, startSec: offset + 2*BAR + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: D4, startSec: offset + 2*BAR + 0.80, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: F4, startSec: offset + 2*BAR + 1.00, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: E4, startSec: offset + 2*BAR + 1.20, durationSec: Q,  gain: 0.28, wave: 'square' },

    // Bar 4 — G — tension → resolution pull
    { freq: G3, startSec: offset + 3*BAR + 0.00, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: B3, startSec: offset + 3*BAR + 0.40, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: D4, startSec: offset + 3*BAR + 0.60, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: G4, startSec: offset + 3*BAR + 0.80, durationSec: H,  gain: 0.30, wave: 'square' },

    ...drums4(offset),
  ];
}

// Cycle 3 (bars 9-12, offset 12.8): high pitch frenzy
function cycle3(offset: number): Note[] {
  return [
    // Bar 1 — high Am stabs
    { freq: E5, startSec: offset + 0.00, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: A4, startSec: offset + 0.20, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: C5, startSec: offset + 0.40, durationSec: Q,  gain: 0.28, wave: 'square' },
    { freq: E5, startSec: offset + 0.80, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: D5, startSec: offset + 1.00, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: C5, startSec: offset + 1.20, durationSec: Q,  gain: 0.28, wave: 'square' },

    // Bar 2 — B4 chromatic touch (b7 of Am = G, tension note)
    { freq: B4, startSec: offset + BAR + 0.00, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: A4, startSec: offset + BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: G4, startSec: offset + BAR + 0.40, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: F4, startSec: offset + BAR + 0.60, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: E4, startSec: offset + BAR + 0.80, durationSec: H,  gain: 0.30, wave: 'square' },

    // Bar 3 — F major driving
    { freq: F4, startSec: offset + 2*BAR + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: A4, startSec: offset + 2*BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: C5, startSec: offset + 2*BAR + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: F5, startSec: offset + 2*BAR + 0.80, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: E5, startSec: offset + 2*BAR + 1.00, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: D5, startSec: offset + 2*BAR + 1.20, durationSec: Q,  gain: 0.28, wave: 'square' },

    // Bar 4 — G → Am anticipation
    { freq: G4, startSec: offset + 3*BAR + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: A4, startSec: offset + 3*BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: G5, startSec: offset + 3*BAR + 0.40, durationSec: Q,  gain: 0.28, wave: 'square' },
    { freq: G4, startSec: offset + 3*BAR + 0.80, durationSec: H,  gain: 0.26, wave: 'square' },

    ...drums4(offset),
  ];
}

// Cycle 4 (bars 13-16, offset 19.2): low bass growl callback
function cycle4(offset: number): Note[] {
  return [
    ...cycle1(offset).filter(n => n.freq !== 55 && n.freq !== 110), // reuse melody, no drums (add separately)
    ...drums4(offset),
  ];
}

// Cycle 5 (bars 17-20, offset 25.6): repeat cycle 3 one octave lower
function cycle5(offset: number): Note[] {
  // Transpose cycle3 melody down one octave (÷2) then add drums
  return [
    { freq: E4, startSec: offset + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: A3, startSec: offset + 0.20, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: C4, startSec: offset + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: E4, startSec: offset + 0.80, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: D4, startSec: offset + 1.00, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: C4, startSec: offset + 1.20, durationSec: Q,  gain: 0.28, wave: 'square' },

    { freq: B3, startSec: offset + BAR + 0.00, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: A3, startSec: offset + BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: G3, startSec: offset + BAR + 0.40, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: F3, startSec: offset + BAR + 0.60, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: E3, startSec: offset + BAR + 0.80, durationSec: H,  gain: 0.30, wave: 'square' },

    { freq: F3, startSec: offset + 2*BAR + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: A3, startSec: offset + 2*BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: C4, startSec: offset + 2*BAR + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: F4, startSec: offset + 2*BAR + 0.80, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: E4, startSec: offset + 2*BAR + 1.00, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: D4, startSec: offset + 2*BAR + 1.20, durationSec: Q,  gain: 0.28, wave: 'square' },

    { freq: G3, startSec: offset + 3*BAR + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: A3, startSec: offset + 3*BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: G4, startSec: offset + 3*BAR + 0.40, durationSec: Q,  gain: 0.28, wave: 'square' },
    { freq: G3, startSec: offset + 3*BAR + 0.80, durationSec: H,  gain: 0.26, wave: 'square' },

    ...drums4(offset),
  ];
}

// Cycle 6 (bars 21-24, offset 32.0): climax — high Am unison with octave bass
function cycle6(offset: number): Note[] {
  return [
    // Melody — rapid repeating A motif
    { freq: A4, startSec: offset + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: A4, startSec: offset + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: C5, startSec: offset + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: E5, startSec: offset + 0.80, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: D5, startSec: offset + 1.00, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: C5, startSec: offset + 1.20, durationSec: Q,  gain: 0.28, wave: 'square' },

    { freq: B4, startSec: offset + BAR + 0.00, durationSec: Q,  gain: 0.28, wave: 'square' },
    { freq: A4, startSec: offset + BAR + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: G4, startSec: offset + BAR + 0.80, durationSec: Q,  gain: 0.28, wave: 'square' },
    { freq: A4, startSec: offset + BAR + 1.20, durationSec: Q,  gain: 0.30, wave: 'square' },

    { freq: E5, startSec: offset + 2*BAR + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: F5, startSec: offset + 2*BAR + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: E5, startSec: offset + 2*BAR + 0.40, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: C5, startSec: offset + 2*BAR + 0.80, durationSec: H,  gain: 0.28, wave: 'square' },

    { freq: D5, startSec: offset + 3*BAR + 0.00, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: E5, startSec: offset + 3*BAR + 0.20, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: A4, startSec: offset + 3*BAR + 0.40, durationSec: H,  gain: 0.30, wave: 'square' },
    { freq: G4, startSec: offset + 3*BAR + 1.20, durationSec: Q,  gain: 0.26, wave: 'square' },

    // Octave bass
    { freq: A2, startSec: offset + 0.00,       durationSec: H,  gain: 0.20, wave: 'square' },
    { freq: A2, startSec: offset + BAR,         durationSec: H,  gain: 0.20, wave: 'square' },
    { freq: A2, startSec: offset + 2*BAR,       durationSec: H,  gain: 0.20, wave: 'square' },
    { freq: A2, startSec: offset + 3*BAR,       durationSec: H,  gain: 0.20, wave: 'square' },

    ...drums4(offset),
  ];
}

// Final 4-bar coda (bars 25-28, offset 38.4 → 44.8 s < 45 s)
function bossCoda(offset: number): Note[] {
  return [
    // Rapid descent Am scale
    { freq: A4, startSec: offset + 0.00, durationSec: E,  gain: 0.30, wave: 'square' },
    { freq: G4, startSec: offset + 0.20, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: F4, startSec: offset + 0.40, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: E4, startSec: offset + 0.60, durationSec: E,  gain: 0.28, wave: 'square' },
    { freq: D4, startSec: offset + 0.80, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: C4, startSec: offset + 1.00, durationSec: E,  gain: 0.26, wave: 'square' },
    { freq: B3, startSec: offset + 1.20, durationSec: E,  gain: 0.24, wave: 'square' },
    { freq: A3, startSec: offset + 1.40, durationSec: E,  gain: 0.28, wave: 'square' },

    { freq: A3, startSec: offset + BAR + 0.00, durationSec: H,  gain: 0.30, wave: 'square' },
    { freq: E4, startSec: offset + BAR + 0.80, durationSec: H,  gain: 0.28, wave: 'square' },

    { freq: A3, startSec: offset + 2*BAR + 0.00, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: C4, startSec: offset + 2*BAR + 0.40, durationSec: Q,  gain: 0.28, wave: 'square' },
    { freq: E4, startSec: offset + 2*BAR + 0.80, durationSec: Q,  gain: 0.30, wave: 'square' },
    { freq: A4, startSec: offset + 2*BAR + 1.20, durationSec: Q,  gain: 0.28, wave: 'square' },

    // Final bar — sustained low A with drum; extend to fill up to 45 s
    { freq: A3, startSec: offset + 3*BAR + 0.00, durationSec: 1.80, gain: 0.30, wave: 'square' },
    { freq: A2, startSec: offset + 3*BAR + 0.00, durationSec: 1.80, gain: 0.20, wave: 'square' },

    ...drums4(offset),
  ];
}

// 45 s / 1.60 s per bar = 28.125 bars → use 28 bars = 44.8 s (fits within 45 s)
// 6 cycles × 4 bars × 1.6 s = 38.4 s, then coda 4 bars = 6.4 s → 44.8 s total
export const BOSS_LOOP_NOTES: Note[] = [
  ...cycle1(0),
  ...cycle2(6.4),
  ...cycle3(12.8),
  ...cycle4(19.2),
  ...cycle5(25.6),
  ...cycle6(32.0),
  ...bossCoda(38.4),
];

export const BOSS_LOOP_DURATION_SEC = 45;
